import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Volume2, VolumeX, Play, Pause, Loader2, Sparkles, ArrowRight, PartyPopper } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTour } from '../contexts/TourContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── The guided tour overlay. Mounted ONCE at App level so it survives route changes.
export function SampleDataTour() {
  const { active, resumeMode, stopTour, primedAudioRef } = useTour();
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioPaused, setAudioPaused] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const audioRef = useRef(null);
  const wakeLockRef = useRef(null);
  const generationStartedRef = useRef(false);
  const goNextRef = useRef(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ─── Fetch steps + kick off audio generation whenever tour activates ───
  useEffect(() => {
    if (!active) {
      // Reset on close
      setCurrentStep(0);
      setSteps([]);
      setAudioReady(false);
      setShowCompletion(false);
      generationStartedRef.current = false;
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tour/steps`);
        setSteps(data.steps || []);
        // Resume mode: pick up where the user left off. Otherwise start from 0.
        const savedStep = Math.max(0, Math.min(data.current_step || 0, (data.steps || []).length - 1));
        setCurrentStep(resumeMode && savedStep > 0 && !data.completed ? savedStep : 0);
        setAudioReady(!!data.has_audio);

        // If audio isn't generated yet, kick off the background job + poll
        if (!data.has_audio && !generationStartedRef.current) {
          generationStartedRef.current = true;
          setGenerating(true);
          try { await axios.post(`${API}/api/tour/generate-audio`); } catch {}
          // Also fire off mini-tour audio gen — idempotent, only fills gaps
          try { await axios.post(`${API}/api/tour/mini/generate-audio`); } catch {}
          // Poll status
          const poll = setInterval(async () => {
            try {
              const { data: st } = await axios.get(`${API}/api/tour/audio-status`);
              if (st.generated) {
                clearInterval(poll);
                setGenerating(false);
                setAudioReady(true);
                // Refetch step metadata so has_audio flags are true
                try {
                  const { data: d2 } = await axios.get(`${API}/api/tour/steps`);
                  setSteps(d2.steps || []);
                } catch {}
              }
            } catch {}
          }, 3000);
          // Stop polling after 2 minutes to avoid runaway
          setTimeout(() => { clearInterval(poll); setGenerating(false); }, 120000);
        }
      } catch {}
    })();
  }, [active, resumeMode]);

  // ─── Position spotlight on the target element (with retry for late-rendering pages) ───
  // v2.5.895 — Take TWO forms of updater:
  //   • `updateSpotlight`     — just re-measures the rect (used on scroll/resize).
  //   • `updateSpotlightAndScroll` — measures AND scrolls target into view (used only
  //     when the step changes). The old code called scrollIntoView on every scroll
  //     event, which caused a smooth-scroll → scroll-event → smooth-scroll feedback
  //     loop that looked like a blinking / jittery background.
  const updateSpotlight = useCallback(() => {
    if (!active || !steps[currentStep]) { setSpotlightRect(null); return; }
    const step = steps[currentStep];
    if (step.position === 'center') { setSpotlightRect(null); return; }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) { setSpotlightRect(null); return; }
      setSpotlightRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      });
    } else {
      setSpotlightRect(null);
    }
  }, [active, currentStep, steps]);

  const updateSpotlightAndScroll = useCallback(() => {
    if (!active || !steps[currentStep]) return;
    const step = steps[currentStep];
    if (step.position === 'center') { setSpotlightRect(null); return; }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) { setSpotlightRect(null); return; }
      setSpotlightRect({ top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16 });
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [active, currentStep, steps]);

  useEffect(() => {
    if (!active) return;
    // Scroll INTO VIEW only on step change (not on every scroll event).
    const timers = [
      setTimeout(updateSpotlightAndScroll, 300),
      setTimeout(updateSpotlightAndScroll, 900),
      setTimeout(updateSpotlightAndScroll, 1800),
    ];
    // On scroll/resize we only re-measure — no scrolling back — otherwise the
    // spotlight fights the user's own scrolling and looks like it blinks.
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight, true);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight, true);
    };
  }, [active, currentStep, updateSpotlight, updateSpotlightAndScroll, location.pathname]);

  // ─── Navigate to the step's page if the user isn't already there ───
  useEffect(() => {
    if (!active || !steps[currentStep]) return;
    const step = steps[currentStep];
    if (step.page && location.pathname !== step.page) {
      navigate(step.page);
    }
  }, [active, currentStep, steps, navigate, location.pathname]);

  // ─── Wake Lock (keep screen awake while narration plays) ───
  const requestWakeLock = async () => {
    try { if ('wakeLock' in navigator) wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch {}
  };
  const releaseWakeLock = () => {
    if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
  };

  // ─── Track step view for analytics ───
  const trackStep = useCallback((stepId, action = 'view') => {
    axios.post(`${API}/api/tour/track-step`, { step_id: stepId, tour_type: 'main', action }).catch(() => {});
  }, []);

  // ─── Play audio using the primed HTMLAudioElement from TourContext ───
  const playStepAudio = useCallback(async (stepId) => {
    if (audioMuted) return;
    // Stop current audio
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setAudioPlaying(false);

    setAudioLoading(true);
    try {
      // v2.5.757 — direct static mp3 URL (browser-cached, zero API cost on replay).
      const src = `${API}/api/tour/audio-file/${stepId}.mp3`;

      // Reuse the primed audio element (which has autoplay permission from the
      // initial user gesture). If it's missing, fall back to a new Audio and
      // hope the browser allows it.
      const audio = primedAudioRef?.current || new Audio();
      audio.src = src;
      audio.volume = 1;
      audio.currentTime = 0;
      audioRef.current = audio;
      audio.onended = () => {
        setAudioPlaying(false);
        setAudioPaused(false);
        releaseWakeLock();
        // Auto-advance to next step after narration finishes (only if still playing this audio)
        if (audioRef.current === audio) {
          setTimeout(() => { if (goNextRef.current) goNextRef.current(); }, 800);
        }
      };
      audio.onerror = () => { setAudioPlaying(false); setAudioPaused(false); releaseWakeLock(); };
      try {
        await audio.play();
        await requestWakeLock();
        setAudioPlaying(true);
        setAudioPaused(false);
      } catch {
        // Autoplay blocked — surface nothing (user can click Replay manually)
        setAudioPlaying(false);
      }
    } catch {}
    finally { setAudioLoading(false); }
  }, [audioMuted, primedAudioRef]);

  // ─── Auto-play narration when the step changes (audio must already be ready) ───
  useEffect(() => {
    if (!active || !steps[currentStep]) return;
    trackStep(steps[currentStep].id, 'view');
    if (!audioMuted && audioReady && steps[currentStep].has_audio) {
      const timer = setTimeout(() => playStepAudio(steps[currentStep].id), 500);
      return () => clearTimeout(timer);
    }
  }, [active, currentStep, steps, playStepAudio, audioMuted, audioReady, trackStep]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setAudioPlaying(false);
    setAudioPaused(false);
    releaseWakeLock();
  };

  // Pause / resume the current narration without advancing steps
  const togglePauseAudio = () => {
    const a = audioRef.current;
    if (!a) {
      // No audio loaded yet — start narration for the current step
      if (steps[currentStep]?.has_audio) playStepAudio(steps[currentStep].id);
      return;
    }
    if (a.paused) {
      a.play().then(() => {
        setAudioPaused(false);
        setAudioPlaying(true);
      }).catch(() => {});
    } else {
      a.pause();
      setAudioPaused(true);
      setAudioPlaying(false);
    }
  };

  const goNext = () => {
    stopAudio();
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      axios.post(`${API}/api/tour/progress`, { current_step: next }).catch(() => {});
    } else {
      completeTour();
    }
  };

  // Keep latest goNext in a ref so audio.onended can auto-advance without stale closure
  useEffect(() => { goNextRef.current = goNext; });

  const goPrev = () => {
    stopAudio();
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const completeTour = () => {
    stopAudio();
    axios.post(`${API}/api/tour/progress`, { current_step: steps.length, completed: true }).catch(() => {});
    // Show celebration + deep-link CTA instead of closing immediately
    setShowCompletion(true);
  };

  const closeCompletion = () => {
    setShowCompletion(false);
    stopTour();
  };

  const goToOnboarding = () => {
    setShowCompletion(false);
    stopTour();
    navigate('/overview/onboarding');
  };

  const skipTour = () => {
    stopAudio();
    if (steps[currentStep]) trackStep(steps[currentStep].id, 'skip');
    axios.post(`${API}/api/tour/progress`, { completed: true }).catch(() => {});
    stopTour();
  };

  if (!active) return null;
  if (showCompletion) {
    return <TourCompletionModal onClose={closeCompletion} onPrimary={goToOnboarding} />;
  }
  if (steps.length === 0) return null;

  const step = steps[currentStep];

  // Calculate tooltip position — clamps inside viewport on ALL sides (desktop + mobile)
  const getTooltipStyle = () => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const margin = 16;

    // Approximate tooltip dimensions (must stay in sync with JSX styles)
    const isMobile = vw < 640;
    const tipW = isMobile ? Math.min(vw - margin * 2, 420) : 420;
    const tipH = isMobile ? 360 : 320; // conservative upper bound — overflow scrolls inside card

    // Mobile: dock to bottom-center, full-width. Desktop with no spotlight: center.
    if (isMobile) {
      return {
        top: Math.max(margin, vh - tipH - margin),
        left: margin,
        right: margin,
        width: `calc(100vw - ${margin * 2}px)`,
        maxWidth: '100%',
        maxHeight: `calc(100vh - ${margin * 2}px)`,
        overflowY: 'auto',
      };
    }
    if (!spotlightRect) {
      return {
        top: Math.max(margin, (vh - tipH) / 2),
        left: Math.max(margin, (vw - tipW) / 2),
        width: tipW,
        maxWidth: '420px',
        maxHeight: `calc(100vh - ${margin * 2}px)`,
        overflowY: 'auto',
      };
    }

    // Desktop with spotlight — pick preferred side, flip if it overflows, then clamp
    const pos = step?.position || 'bottom';
    let top, left;
    if (pos === 'bottom') {
      top = spotlightRect.top + spotlightRect.height + margin;
      if (top + tipH + margin > vh) top = spotlightRect.top - tipH - margin;
      left = spotlightRect.left;
    } else if (pos === 'top') {
      top = spotlightRect.top - tipH - margin;
      if (top < margin) top = spotlightRect.top + spotlightRect.height + margin;
      left = spotlightRect.left;
    } else if (pos === 'right') {
      left = spotlightRect.left + spotlightRect.width + margin;
      if (left + tipW + margin > vw) left = spotlightRect.left - tipW - margin;
      top = spotlightRect.top;
    } else if (pos === 'left') {
      left = spotlightRect.left - tipW - margin;
      if (left < margin) left = spotlightRect.left + spotlightRect.width + margin;
      top = spotlightRect.top;
    } else {
      top = spotlightRect.top + spotlightRect.height + margin;
      left = spotlightRect.left;
    }

    // Final hard clamp — tooltip must always fit inside viewport with margin on all sides
    top = Math.min(Math.max(margin, top), Math.max(margin, vh - tipH - margin));
    left = Math.min(Math.max(margin, left), Math.max(margin, vw - tipW - margin));

    return {
      top, left, width: tipW, maxWidth: '420px',
      maxHeight: `calc(100vh - ${margin * 2}px)`,
      overflowY: 'auto',
    };
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'none' }}>
        {/* Dark overlay with cutout */}
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'auto' }}>
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlightRect && (
                <rect
                  x={spotlightRect.left} y={spotlightRect.top}
                  width={spotlightRect.width} height={spotlightRect.height}
                  rx="12" fill="black"
                />
              )}
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#tour-mask)" />
        </svg>

        {/* Spotlight ring */}
        {spotlightRect && (
          <div
            className="absolute border-2 border-emerald-400 rounded-xl shadow-[0_0_0_4px_rgba(16,185,129,0.2)] transition-all duration-500 ease-out"
            style={{
              top: spotlightRect.top, left: spotlightRect.left,
              width: spotlightRect.width, height: spotlightRect.height,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Tooltip Card */}
        <div
          className="absolute z-[9999] bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden animate-fadeIn"
          style={{ ...getTooltipStyle(), pointerEvents: 'auto' }}
          data-testid="tour-tooltip"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 flex items-center justify-between">
            <div>
              <p className="text-white text-[10px] font-medium opacity-80">Step {currentStep + 1} of {steps.length}</p>
              <h3 className="text-white font-bold text-sm">{step?.title}</h3>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={togglePauseAudio}
                className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors"
                title={audioPaused || !audioPlaying ? "Play narration" : "Pause narration"}
                data-testid="tour-pause-btn"
                disabled={audioMuted}
              >
                {audioPaused || !audioPlaying ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
              <button
                onClick={() => { setAudioMuted(!audioMuted); if (!audioMuted) stopAudio(); }}
                className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors"
                title={audioMuted ? "Unmute" : "Mute"}
                data-testid="tour-mute-btn"
              >
                {audioMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <button onClick={skipTour} className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors" title="Skip Tour" data-testid="tour-skip-btn">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            <p className="text-sm text-zinc-600 leading-relaxed">{step?.narration}</p>

            {/* Audio indicator */}
            {(audioPlaying || audioLoading || audioPaused) && (
              <div className="flex items-center gap-2 mt-3 text-xs text-emerald-600">
                {audioLoading ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Loading audio...</>
                ) : audioPaused ? (
                  <><Pause className="h-3 w-3" /> Narration paused — click play to resume</>
                ) : (
                  <><Volume2 className="h-3 w-3 animate-pulse" /> Playing narration...</>
                )}
              </div>
            )}
            {generating && !audioReady && (
              <div className="flex items-center gap-2 mt-3 text-xs text-violet-600">
                <Loader2 className="h-3 w-3 animate-spin" /> Preparing voice narration in the background…
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {Array.from({ length: steps.length }, (_, i) => (
                <div key={i} className={cn("w-1.5 h-1.5 rounded-full transition-colors", i === currentStep ? "bg-emerald-500" : i < currentStep ? "bg-emerald-300" : "bg-zinc-300")} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={goPrev} data-testid="tour-back-btn">
                  <ChevronLeft className="h-3 w-3" /> Back
                </Button>
              )}
              {audioReady && !audioPlaying && !audioMuted && step?.has_audio && (
                <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => playStepAudio(step.id)} data-testid="tour-replay-audio-btn">
                  <Play className="h-3 w-3" /> Replay
                </Button>
              )}
              <Button size="sm" className="h-8 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={goNext} data-testid="tour-next-btn">
                {currentStep < steps.length - 1 ? <>Next <ChevronRight className="h-3 w-3" /></> : 'Finish Tour'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Celebration modal shown after the 14-step tour wraps ──────────────────
function TourCompletionModal({ onClose, onPrimary }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      data-testid="tour-completion-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative">
        {/* Dismiss */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 z-10"
          title="Dismiss"
          data-testid="tour-completion-dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Celebratory header */}
        <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 px-6 pt-8 pb-6 text-center relative overflow-hidden">
          {/* Subtle confetti dots */}
          <div className="absolute inset-0 opacity-20" aria-hidden="true">
            {[...Array(14)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-white"
                style={{
                  top: `${(i * 37) % 90 + 5}%`,
                  left: `${(i * 53) % 90 + 5}%`,
                }}
              />
            ))}
          </div>
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3 ring-4 ring-white/10">
              <PartyPopper className="h-7 w-7 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Tour complete!</h2>
            <p className="text-xs text-emerald-50 mt-1 opacity-90">You've seen every core feature of Konnect.</p>
          </div>
        </div>

        {/* Next-step CTA */}
        <div className="px-6 py-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-zinc-900">Here's what to edit first</h3>
              <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                Your Onboarding Checklist walks you through replacing each sample section with your real company details — in the recommended order.
              </p>
            </div>
          </div>

          <button
            onClick={onPrimary}
            className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-medium text-sm py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
            data-testid="tour-completion-onboarding-btn"
          >
            Open Onboarding Checklist
            <ArrowRight className="h-4 w-4" />
          </button>

          <button
            onClick={onClose}
            className="w-full text-xs text-zinc-500 hover:text-zinc-700 mt-3 py-2"
            data-testid="tour-completion-explore-btn"
          >
            I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── External trigger button — used in the dashboard banner, etc.
export function TourStartButton({ className }) {
  const { startTour } = useTour();
  const [tourMeta, setTourMeta] = useState({ completed: false, current_step: 0, total: 0 });
  const [hasDemoData, setHasDemoData] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/tour/steps`),
      axios.get(`${API}/api/spq/demo-data-status`),
    ]).then(([tour, demo]) => {
      setTourMeta({
        completed: !!tour.data.completed,
        current_step: tour.data.current_step || 0,
        total: (tour.data.steps || []).length,
      });
      setHasDemoData(!!demo.data.active);
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (!hasDemoData && tourMeta.completed) return null;

  const hasPartialProgress = !tourMeta.completed && tourMeta.current_step > 0 && tourMeta.current_step < tourMeta.total;

  let label = 'Start Guided Tour';
  let variant = 'default';
  let onClick = () => startTour({});
  if (tourMeta.completed) {
    label = 'Replay Tour';
    variant = 'outline';
    onClick = () => startTour({ reset: true });
  } else if (hasPartialProgress) {
    label = `Resume Tour (${tourMeta.current_step + 1}/${tourMeta.total})`;
    variant = 'default';
    onClick = () => startTour({ resume: true });
  }

  return (
    <Button
      size="sm"
      className={cn("gap-1.5 text-xs", className)}
      variant={variant}
      onClick={onClick}
      data-testid="start-tour-btn"
    >
      <Play className="h-3 w-3" />
      {label}
    </Button>
  );
}
